import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import services from '../services';
import AppSelect from '../common/AppSelect';
import { parseAssignmentPermissions } from '../utils/ticketMovePermission';

const GREEN = '#1a7d3e';

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,24,39,0.55)',
        zIndex: 9998,
    },
    wrap: {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 9999,
    },
    panel: {
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
        width: '100%',
        maxWidth: 440,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
    },
    title: { margin: 0, fontSize: 18, fontWeight: 600, color: '#1f2937' },
    close: {
        border: 0,
        background: 'transparent',
        fontSize: 22,
        lineHeight: 1,
        color: '#6b7280',
        cursor: 'pointer',
    },
    body: { padding: 20, overflowY: 'auto' },
    label: {
        display: 'block',
        fontSize: 13,
        fontWeight: 600,
        color: '#4b5563',
        marginBottom: 6,
    },
    select: { height: 40, fontSize: 14 },
    field: { marginBottom: 16 },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        padding: '14px 20px',
        borderTop: '1px solid #e5e7eb',
    },
    cancel: {
        height: 38,
        padding: '0 16px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        background: '#fff',
        color: '#374151',
        fontSize: 14,
        cursor: 'pointer',
    },
    move: (enabled) => ({
        height: 38,
        padding: '0 18px',
        border: 0,
        borderRadius: 8,
        background: GREEN,
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        opacity: enabled ? 1 : 0.5,
        cursor: enabled ? 'pointer' : 'not-allowed',
    }),
};

const MoveAllTicketsModal = ({ fromListId, fromBoardId, isAdmin, userId, onClose, onMoved }) => {
    const [allBoards, setAllBoards] = useState([]);
    // Non-admins: per-board assignment permissions ("can add tickets to" lists)
    const [assignments, setAssignments] = useState(null);
    const [boardId, setBoardId] = useState(fromBoardId || '');
    const [listId, setListId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const canMove = !!listId && !submitting;

    // Every active board with its lists (not limited to the user's own board access)
    useEffect(() => {
        (async () => {
            try {
                const response = await services.board.GET_BOARD_LIST();
                setAllBoards(response?.data?.data || []);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    useEffect(() => {
        if (isAdmin || !userId) return;
        services.userassignboard
            .GET_UserAssignBoard_ID(userId)
            .then((res) => setAssignments(res?.data?.data || []))
            .catch((e) => {
                console.error(e);
                setAssignments([]);
            });
    }, [isAdmin, userId]);

    // Lists the user may move tickets into, per board. Admins: everything.
    const allowedLists = (board) => {
        const boardLists = board.BoardLists || [];
        if (isAdmin) return boardLists;
        const assignment = (assignments || []).find((a) => a.boardId === board.id);
        if (!assignment) return [];
        const entries = parseAssignmentPermissions(assignment.permissions).addTicket;
        const ids = entries.map((e) => (e && typeof e === 'object' ? e.value ?? e.id : e));
        return ids.includes('*') ? boardLists : boardLists.filter((l) => ids.includes(l.id));
    };

    const boards = allBoards
        .filter((b) => allowedLists(b).some((l) => l.id !== fromListId))
        .map(({ boardName, id }) => ({ label: boardName, value: id }));
    const selectedBoard = allBoards.find((b) => b.id === boardId);
    const lists = (selectedBoard ? allowedLists(selectedBoard) : [])
        .filter((item) => item.id !== fromListId)
        .slice()
        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
        .map((item) => ({ value: item.id, label: item.coloum }));

    useEffect(() => {
        setListId('');
    }, [boardId]);

    const handleMove = async () => {
        if (!canMove) return;
        setSubmitting(true);
        try {
            const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/task/moveAll`, {
                fromListId,
                toListId: listId,
            });
            const blocked = data?.blocked?.length || 0;
            toast.success(`${data?.moved || 0} ticket(s) moved.`);
            if (blocked) toast.warn(`${blocked} ticket(s) could not be moved (mandatory checklist pending).`);
            onMoved();
            onClose();
        } catch (error) {
            console.error('Error moving all tickets:', error);
            toast.error(error?.response?.data?.message || 'Failed to move tickets.');
        } finally {
            setSubmitting(false);
        }
    };

    return ReactDOM.createPortal(
        <div role="dialog" aria-modal="true" aria-labelledby="move-all-title">
            <div style={styles.backdrop} onClick={onClose} />
            <div style={styles.wrap}>
                <div style={styles.panel}>
                    <div style={styles.header}>
                        <h3 id="move-all-title" style={styles.title}>
                            Move all tickets
                        </h3>
                        <button type="button" style={styles.close} onClick={onClose} aria-label="Close">
                            &times;
                        </button>
                    </div>

                    <div style={styles.body}>
                        <div style={styles.field}>
                            <label style={styles.label}>Board</label>
                            <AppSelect
                                portal
                                minWidth="100%"
                                value={boardId}
                                onChange={(e) => setBoardId(e.target.value)}
                                placeholder="Select board"
                                style={styles.select}
                                options={boards}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>List</label>
                            <AppSelect
                                portal
                                minWidth="100%"
                                value={listId}
                                onChange={(e) => setListId(e.target.value)}
                                placeholder={boardId ? 'Select list' : 'Select a board first'}
                                disabled={!boardId}
                                style={styles.select}
                                options={lists}
                            />
                        </div>
                    </div>

                    <div style={styles.footer}>
                        <button type="button" style={styles.cancel} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" style={styles.move(canMove)} disabled={!canMove} onClick={handleMove}>
                            {submitting ? 'Moving...' : 'Move all'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

MoveAllTicketsModal.propTypes = {
    fromListId: PropTypes.string.isRequired,
    fromBoardId: PropTypes.string,
    isAdmin: PropTypes.bool,
    userId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onMoved: PropTypes.func.isRequired,
};

export default MoveAllTicketsModal;
